import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CookieConsent } from "@/components/CookieConsent";
import { CartProvider } from "@/lib/cart";
import { AuthProvider } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { trackPageVisit } from "@/lib/vendor-visits.functions";
import { logNotFound } from "@/lib/not-found.functions";
import { trackPageView } from "@/lib/analytics";
import { categoryCountsQueryOptions } from "@/lib/categories.functions";
// Logotip za JSON-LD (raster PNG, kot ga priporoča Google) — namerno kot niz,
// da se datoteka ne uvozi kot sredstvo in ne sproži nepotrebnega prednalaganja.
const logoAsset = {
  url: "/__l5e/assets-v1/c558daf6-6de8-455f-8f12-c9bbfda27fe2/kupish-logo.png",
};


function NotFoundComponent() {
  const logNotFoundFn = useServerFn(logNotFound);
  const router = useRouter();
  useEffect(() => {
    if (typeof window === "undefined") return;
    void logNotFoundFn({
      data: {
        path: window.location.pathname + window.location.search,
        referrer: document.referrer || null,
        userAgent: navigator.userAgent || null,
      },
    }).catch(() => {});
  }, [logNotFoundFn]);
  const popularCats = [
    { slug: "drogerija", name: "Drogerija" },
    { slug: "elektronika", name: "Elektronika" },
    { slug: "avto", name: "Avto" },
    { slug: "dom-vrt", name: "Dom & Vrt" },
    { slug: "moda", name: "Moda" },
    { slug: "sport", name: "Šport" },
  ];
  const popularArticles = [
    { slug: "priprava-izdelka-za-splet", title: "Priprava izdelka za splet" },
    { slug: "optimizacija-fotografij-izdelka", title: "Optimizacija fotografij izdelka" },
    { slug: "optimizacija-besedila", title: "Optimizacija besedila izdelka" },
    { slug: "seo-osnove-za-trgovce", title: "SEO osnove za trgovce" },
    { slug: "marketing-za-spletne-trgovine", title: "Marketing za spletne trgovine" },
    { slug: "darila-za-obletnico", title: "Ideje za darila za obletnico" },
  ];
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = (new FormData(e.currentTarget).get("q") as string | null)?.trim();
    if (!q) return;
    router.navigate({ to: "/izdelki", search: { q } as never });
  };
  return (
    <div className="mx-auto max-w-3xl px-6 py-16 md:py-24 text-center">
      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-tone-2-soft mb-6">
        <span className="font-serif text-5xl text-brand">?</span>
      </div>
      <div className="font-serif text-8xl md:text-9xl text-brand leading-none">404</div>
      <h1 className="font-serif text-3xl md:text-4xl mt-6">Te strani ni mogoče najti</h1>
      <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
        Stran, ki jo iščete, ne obstaja, je bila premaknjena ali pa je povezava napačna.
        Poskusite z iskalnikom, preglejte priljubljene kategorije ali branje.
      </p>

      <form onSubmit={onSubmit} role="search" className="mt-8 mx-auto flex max-w-md items-stretch gap-2">
        <label htmlFor="notfound-search" className="sr-only">Iskanje po strani</label>
        <input
          id="notfound-search"
          name="q"
          type="search"
          autoComplete="off"
          placeholder="Kaj iščete? npr. nakit, darilo, sveče…"
          className="flex-1 rounded-sm border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:border-accent"
        />
        <button
          type="submit"
          className="bg-primary text-primary-foreground px-5 py-2.5 text-sm tracking-wider uppercase hover:opacity-90"
        >
          Išči
        </button>
      </form>

      <div className="mt-8 flex flex-wrap justify-center gap-2">
        {popularCats.map((c) => (
          <Link
            key={c.slug}
            to="/kategorije/$slug"
            params={{ slug: c.slug }}
            className="inline-flex items-center rounded-full border border-border bg-background px-4 py-2 text-sm hover:border-accent hover:text-accent transition-colors"
          >
            {c.name}
          </Link>
        ))}
      </div>

      <div className="mt-10 text-left mx-auto max-w-xl">
        <h2 className="font-serif text-xl text-center mb-4">Priljubljeno branje</h2>
        <ul className="divide-y divide-border border border-border rounded-sm bg-background">
          {popularArticles.map((a) => (
            <li key={a.slug}>
              <Link
                to="/clanek/$slug"
                params={{ slug: a.slug }}
                className="block px-4 py-3 text-sm text-brand hover:bg-muted transition-colors"
              >
                {a.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <Link to="/" className="bg-primary text-primary-foreground px-5 py-2.5 text-sm tracking-wider uppercase hover:opacity-90">
          Domov
        </Link>
        <Link to="/izdelki" className="border border-border px-5 py-2.5 text-sm tracking-wider uppercase hover:border-accent hover:text-accent transition-colors">
          Vsi izdelki
        </Link>
        <Link to="/navdih" className="border border-border px-5 py-2.5 text-sm tracking-wider uppercase hover:border-accent hover:text-accent transition-colors">
          Navdih & zgodbe
        </Link>
      </div>
    </div>
  );
}


function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-2xl">Nekaj je šlo narobe</h1>
        <p className="mt-2 text-sm text-muted-foreground">Poskusite znova ali se vrnite domov.</p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-sm hover:opacity-90"
          >
            Poskusi znova
          </button>
          <a href="/" className="px-4 py-2 border border-border text-sm rounded-sm hover:bg-muted">
            Domov
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0284C7" },
      { name: "google-site-verification", content: "ktt2A6iE8Axw5YCfvf8Jp7P-9D_JydgiDoIvWGJtO0Y" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "kupish.si" },
      { property: "og:locale", content: "sl_SI" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap",
        media: "print",
        id: "gfonts",
      },

      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
    ],

    scripts: [
      {
        children:
          "(function(){var l=document.getElementById('gfonts');if(l){l.media='all';}})();",
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://kupish.si/#organization",
          name: "kupish.si",
          alternateName: "kupish.si – novi izdelki različnih trgovin",
          url: "https://kupish.si/",
          logo: {
            "@type": "ImageObject",
            url: `https://kupish.si${logoAsset.url}`,
          },
          image: `https://kupish.si${logoAsset.url}`,
          description:
            "Spletna trgovina kupish.si zbirka novih izdelkov različnih ponudnikov in trgovin: nakit, moda, dom, elektronika, kozmetika, darila in še več.",
          email: "info@kupish.si",
          inLanguage: "sl-SI",
          areaServed: { "@type": "Country", name: "Slovenija" },
          knowsAbout: [
            "novi izdelki",
            "spletna trgovina",
            "nakit",
            "moda",
            "dom in ambient",
            "elektronika",
            "darila",
          ],
          sameAs: [
            "https://www.facebook.com/kupish.si",
            "https://www.instagram.com/kupish.si",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://kupish.si/#website",
          name: "kupish.si",
          url: "https://kupish.si/",
          inLanguage: "sl-SI",
          publisher: { "@id": "https://kupish.si/#organization" },
          potentialAction: {
            "@type": "SearchAction",
            target: "https://kupish.si/izdelki?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(categoryCountsQueryOptions());
    return {};
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="sl">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const trackPage = useServerFn(trackPageVisit);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      router.invalidate();
      queryClient.invalidateQueries();
    });
    return () => subscription.unsubscribe();
  }, [router, queryClient]);

  // Site-wide page-view tracking (excludes shop/product/article — those are tracked separately)
  useEffect(() => {
    const fire = () => {
      const path = window.location.pathname;
      if (/^\/(prodajalci|izdelki|clanek)\//.test(path)) return;
      if (/^\/_authenticated/.test(path)) return;
      void trackPage({ data: { kind: "site", path } }).catch(() => {});
    };
    fire();
    const unsub = router.subscribe("onResolved", fire);
    return () => unsub();
  }, [router, trackPage]);

  // Google Analytics page views on client-side navigation
  // (initial load is tracked automatically by gtag.js config)
  useEffect(() => {
    const unsub = router.subscribe("onResolved", () => {
      trackPageView(window.location.pathname + window.location.search);
    });
    return () => unsub();
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <div className="min-h-screen flex flex-col">
            <SiteHeader />
            <main className="flex-1">
              <Outlet />
            </main>
            <SiteFooter />
            <CookieConsent />
          </div>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
